import React, { useEffect, useState, useCallback } from 'react';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import { openAlert, openConfirm } from '../../utils/common';
import Modal from '../../components/Layout/Modal';
import medicalStaffService from '../../services/medicalStaffService';

const ROLE_OPTIONS = [
  { value: 'doctor', label: '의사' },
  { value: 'nurse', label: '간호사' },
  { value: 'technician', label: '의료기사' },
  { value: 'admin', label: '관리자' }
];

const BYPASS_LEVEL_OPTIONS = [
  { value: 1, label: '레벨 1 (기본)' },
  { value: 2, label: '레벨 2 (중간)' },
  { value: 3, label: '레벨 3 (높음)' }
];

const ALLOWED_FEATURES = [
  { value: 'root_bypass', label: '루팅 우회' },
  { value: 'debug_bypass', label: '디버그 우회' },
  { value: 'emulator_bypass', label: '에뮬레이터 우회' }
];

const MedicalStaffPage = () => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [allChecked, setAllChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showUserSearchModal, setShowUserSearchModal] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    isActive: '',
    role: ''
  });

  const [staffData, setStaffData] = useState({
    id: '',
    userIdx: '',
    userName: '',
    staffId: '',
    name: '',
    phoneNumber: '',
    email: '',
    department: '',
    role: 'doctor',
    bypassLevel: 1,
    maxDailyBypasses: 5,
    sessionDurationMinutes: 30,
    allowedFeatures: ['root_bypass'],
    isEdit: false
  });

  const fetchStaffList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        ...filters
      };
      
      // 빈 문자열 필터 제거
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });
      
      const result = await medicalStaffService.getStaffList(params);
      if (result.success) {
        setStaffList(result.data.staff || []);
        setPagination(prev => ({
          ...prev,
          ...result.data.pagination
        }));
      } else {
        openAlert({ title: '알림', description: result.message });
      }
    } catch (e) {
      openAlert({ title: '알림', description: '데이터 로드 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters]);

  useEffect(() => {
    fetchStaffList();
  }, [fetchStaffList]);

  const handleAllChange = e => {
    const checked = e.target.checked;
    setAllChecked(checked);
    setSelectedIds(checked ? staffList.map(item => item.staff_id) : []);
  };

  const handleSelect = staffId => {
    let next = [...selectedIds];
    if (next.includes(staffId)) next = next.filter(x => x !== staffId);
    else next.push(staffId);
    setSelectedIds(next);
    setAllChecked(next.length === staffList.length);
  };

  const handleBulkDeleteConfirm = () => {
    if (selectedIds.length === 0) return openAlert({ title: '알림', description: '삭제할 의료진을 선택해주세요.' });
    openConfirm({
      title: '삭제하기',
      description: '선택된 의료진을 삭제하시겠습니까?',
      positive_method: handleBulkDelete,
      positive_param: selectedIds,
      positive_btn: '확인',
      negative_btn: '취소',
    });
  };

  const handleBulkDelete = async ids => {
    setLoading(true);
    try {
      for (const staffId of ids) {
        await medicalStaffService.deleteStaff(staffId);
      }
      openAlert({ title: '성공', description: '선택된 의료진이 삭제되었습니다.' });
      setSelectedIds([]);
      setAllChecked(false);
      fetchStaffList();
    } catch (e) {
      openAlert({ title: '알림', description: '삭제 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setStaffData({
      id: '',
      userIdx: '',
      userName: '',
      staffId: '',
      name: '',
      phoneNumber: '',
      email: '',
      department: '',
      role: 'doctor',
      bypassLevel: 1,
      maxDailyBypasses: 5,
      sessionDurationMinutes: 30,
      allowedFeatures: ['root_bypass'],
      isEdit: false
    });
    setShowModal(true);
  };

  const handleOpenEdit = staff => {
    setStaffData({
      id: staff.id,
      userIdx: staff.user_idx,
      userName: '',
      staffId: staff.staff_id,
      name: staff.name,
      phoneNumber: '', // 보안상 표시하지 않음
      email: staff.email || '',
      department: staff.department || '',
      role: staff.role || 'doctor',
      bypassLevel: staff.bypass_level || 1,
      maxDailyBypasses: staff.max_daily_bypasses || 5,
      sessionDurationMinutes: staff.session_duration_minutes || 30,
      allowedFeatures: staff.allowed_features || ['root_bypass'],
      isEdit: true
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleInputChange = (field, value) => {
    setStaffData((prev) => {
      const updated = { ...prev, [field]: value };

      // 우회 레벨 변경 시 허용 기능 자동 조정
      if (field === "bypassLevel") {
        const level = parseInt(value);
        let allowedFeatures = [...prev.allowedFeatures];

        // 레벨에 따라 허용 가능한 기능 제한
        if (level === 1) {
          // 레벨 1: 루팅 우회만 허용
          allowedFeatures = allowedFeatures.filter((f) => f === "root_bypass");
          if (allowedFeatures.length === 0) allowedFeatures = ["root_bypass"];
        } else if (level === 2) {
          // 레벨 2: 루팅 + 디버그 우회 허용
          allowedFeatures = allowedFeatures.filter(
            (f) => f === "root_bypass" || f === "debug_bypass"
          );
          if (allowedFeatures.length === 0) allowedFeatures = ["root_bypass"];
        }
        // 레벨 3: 모든 기능 허용 (필터링 없음)

        updated.allowedFeatures = allowedFeatures;
      }

      return updated;
    });
  };

  const handleFeatureToggle = feature => {
    setStaffData(prev => {
      const level = prev.bypassLevel;

      // 레벨에 따른 기능 제한 검사
      if (feature === "debug_bypass" && level < 2) {
        openAlert({
          title: "알림",
          description: "디버그 우회는 레벨 2 이상에서만 사용 가능합니다.",
        });
        return prev;
      }
      if (feature === "emulator_bypass" && level < 3) {
        openAlert({
          title: "알림",
          description: "에뮬레이터 우회는 레벨 3에서만 사용 가능합니다.",
        });
        return prev;
      }
      
      const features = [...prev.allowedFeatures];
      const index = features.indexOf(feature);
      if (index > -1) {
        features.splice(index, 1);
      } else {
        features.push(feature);
      }
      return { ...prev, allowedFeatures: features };
    });
  };

  const handleSubmit = async () => {
    // 유효성 검사
    if (!staffData.isEdit) {
      if (!staffData.userIdx) {
        return openAlert({ title: '알림', description: '사용자를 선택해주세요.' });
      }
      if (!staffData.staffId) {
        return openAlert({ title: '알림', description: '의료진 ID를 입력해주세요.' });
      }
      if (!staffData.phoneNumber) {
        return openAlert({ title: '알림', description: '전화번호를 입력해주세요.' });
      }
    }
    if (!staffData.name) {
      return openAlert({ title: '알림', description: '이름을 입력해주세요.' });
    }

    setLoading(true);
    try {
      let result;
      if (staffData.isEdit) {
        const updateData = {
          name: staffData.name,
          email: staffData.email || null,
          department: staffData.department || null,
          role: staffData.role,
          bypassLevel: staffData.bypassLevel,
          maxDailyBypasses: staffData.maxDailyBypasses,
          sessionDurationMinutes: staffData.sessionDurationMinutes,
          allowedFeatures: staffData.allowedFeatures
        };
        if (staffData.phoneNumber) {
          updateData.phoneNumber = staffData.phoneNumber;
        }
        result = await medicalStaffService.updateStaff(staffData.staffId, updateData);
      } else {
        result = await medicalStaffService.createStaff(staffData);
      }

      if (result.success) {
        openAlert({ title: '성공', description: result.message });
        handleCloseModal();
        fetchStaffList();
      } else {
        openAlert({ title: '알림', description: result.message });
      }
    } catch (e) {
      openAlert({ title: '알림', description: '저장 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (staff) => {
    const newStatus = !staff.is_active;
    openConfirm({
      title: newStatus ? '활성화' : '비활성화',
      description: `${staff.name}님을 ${newStatus ? '활성화' : '비활성화'}하시겠습니까?`,
      positive_method: async () => {
        setLoading(true);
        try {
          const result = await medicalStaffService.toggleStaffStatus(staff.staff_id, newStatus);
          if (result.success) {
            openAlert({ title: '성공', description: result.message });
            fetchStaffList();
          } else {
            openAlert({ title: '알림', description: result.message });
          }
        } catch (e) {
          openAlert({ title: '알림', description: '상태 변경 중 오류가 발생했습니다.' });
        } finally {
          setLoading(false);
        }
      },
      positive_btn: '확인',
      negative_btn: '취소',
    });
  };

  const handleDeleteSingle = (staff) => {
    openConfirm({
      title: '삭제하기',
      description: `${staff.name}님을 삭제하시겠습니까?`,
      positive_method: async () => {
        setLoading(true);
        try {
          const result = await medicalStaffService.deleteStaff(staff.staff_id);
          if (result.success) {
            openAlert({ title: '성공', description: result.message });
            fetchStaffList();
          } else {
            openAlert({ title: '알림', description: result.message });
          }
        } catch (e) {
          openAlert({ title: '알림', description: '삭제 중 오류가 발생했습니다.' });
        } finally {
          setLoading(false);
        }
      },
      positive_btn: '확인',
      negative_btn: '취소',
    });
  };

  // 사용자 검색
  const handleUserSearch = async () => {
    if (!userSearchQuery.trim()) return;
    
    setUserSearchLoading(true);
    try {
      const result = await medicalStaffService.searchUsers(userSearchQuery);
      if (result.success) {
        setUserSearchResults(result.data);
      } else {
        openAlert({ title: '알림', description: result.message });
      }
    } catch (e) {
      openAlert({ title: '알림', description: '사용자 검색 중 오류가 발생했습니다.' });
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    setStaffData(prev => ({
      ...prev,
      userIdx: user.idx,
      userName: user.name || user.id,
      name: user.name || ''
    }));
    setShowUserSearchModal(false);
    setUserSearchQuery('');
    setUserSearchResults([]);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ko-KR');
  };

  const getRoleLabel = (role) => {
    const found = ROLE_OPTIONS.find(r => r.value === role);
    return found ? found.label : role;
  };

  return (
    <SectionContainer title="의료진 관리" className="list">
      <Spinner visible={loading} />

      {/* 검색/필터 영역 - 모바일 코드 정책 관리와 동일한 레이아웃 */}
      <div className="search_area">
        <div className="acc_total">
          <h4>
            <b>{pagination.total || staffList.length}</b>개 검색되었습니다.
          </h4>
        </div>
        <div className="left">
          <div className="select_box">
            <select
              value={filters.isActive}
              onChange={(e) => handleFilterChange("isActive", e.target.value)}
            >
              <option value="">전체 상태</option>
              <option value="true">활성</option>
              <option value="false">비활성</option>
            </select>
          </div>
          <div className="select_box" style={{ marginLeft: "10px" }}>
            <select
              value={filters.role}
              onChange={(e) => handleFilterChange("role", e.target.value)}
            >
              <option value="">전체 역할</option>
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="search_wrap insert-wrap">
            <div className="search">
              <div className="input">
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  onKeyUp={(e) => e.key === "Enter" && fetchStaffList()}
                  placeholder="검색 (ID, 이름, 부서)"
                  maxLength="100"
                />
              </div>
              <i onClick={() => fetchStaffList()}>
                <svg
                  width="25"
                  height="26"
                  viewBox="0 0 25 26"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M24.6582 21.6162L19.79 16.748C19.5703 16.5283 19.2725 16.4062 18.96 16.4062H18.1641C19.5117 14.6826 20.3125 12.5146 20.3125 10.1562C20.3125 4.5459 15.7666 0 10.1562 0C4.5459 0 0 4.5459 0 10.1562C0 15.7666 4.5459 20.3125 10.1562 20.3125C12.5146 20.3125 14.6826 19.5117 16.4062 18.1641V18.96C16.4062 19.2725 16.5283 19.5703 16.748 19.79L21.6162 24.6582C22.0752 25.1172 22.8174 25.1172 23.2715 24.6582L24.6533 23.2764C25.1123 22.8174 25.1123 22.0752 24.6582 21.6162ZM10.1562 16.4062C6.7041 16.4062 3.90625 13.6133 3.90625 10.1562C3.90625 6.7041 6.69922 3.90625 10.1562 3.90625C13.6084 3.90625 16.4062 6.69922 16.4062 10.1562C16.4062 13.6084 13.6133 16.4062 10.1562 16.4062Z"
                    fill="#45A3FB"
                  ></path>
                </svg>
              </i>
            </div>
          </div>
          <div className="insert-wrap">
            <button
              className="btn btn-md primary mr-1"
              onClick={() => {
                setFilters({ search: "", isActive: "", role: "" });
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
            >
              초기화
            </button>
          </div>
          <div className="but-flex">
            <button
              className="btn btn-md primary mr-1"
              onClick={handleOpenCreate}
            >
              의료진 등록
            </button>
            <button
              className="btn btn-md danger"
              onClick={handleBulkDeleteConfirm}
              disabled={selectedIds.length === 0}
              style={{
                opacity: selectedIds.length === 0 ? 0.5 : 1,
                cursor: selectedIds.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              선택 삭제 ({selectedIds.length})
            </button>
          </div>
        </div>
      </div>

      {/* 테이블 */}
      <div className="content_area">
        <div className="table_area">
          <div className="table">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={handleAllChange}
                    />
                  </th>
                  <th>No</th>
                  <th>의료진 ID</th>
                  <th>이름</th>
                  <th>부서</th>
                  <th>역할</th>
                  <th>우회 레벨</th>
                  <th>일일 한도</th>
                  <th>상태</th>
                  <th>등록일</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {staffList.length === 0 ? (
                  <tr>
                    <td
                      colSpan="11"
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#6c757d",
                      }}
                    >
                      등록된 의료진이 없습니다.
                    </td>
                  </tr>
                ) : (
                  staffList.map((staff, idx) => (
                    <tr key={staff.staff_id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(staff.staff_id)}
                          onChange={() => handleSelect(staff.staff_id)}
                        />
                      </td>
                      <td>
                        {(pagination.page - 1) * pagination.pageSize + idx + 1}
                      </td>
                      <td>{staff.staff_id}</td>
                      <td>{staff.name}</td>
                      <td>{staff.department || "-"}</td>
                      <td>{getRoleLabel(staff.role)}</td>
                      <td>{staff.bypass_level}</td>
                      <td>{staff.max_daily_bypasses}회</td>
                      <td>
                        <span
                          className={`badge ${
                            staff.is_active ? "bg-success" : "bg-danger"
                          }`}
                        >
                          {staff.is_active ? "활성" : "비활성"}
                        </span>
                      </td>
                      <td>{formatDate(staff.created_at)}</td>
                      <td>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <input
                            type="button"
                            className="btn fourbtn"
                            onClick={() => handleOpenEdit(staff)}
                            value="수정하기"
                          />
                          <input
                            type="button"
                            className={`btn fourbtn ${
                              staff.is_active ? "btn-warning" : "btn-success"
                            }`}
                            onClick={() => handleToggleStatus(staff)}
                            value={staff.is_active ? "비활성화" : "활성화"}
                          />
                          <input
                            type="button"
                            className="btn fourbtn btn-danger"
                            onClick={() => handleDeleteSingle(staff)}
                            value="삭제하기"
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 페이지네이션 */}
      {pagination.totalPages > 1 && (
        <div
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "center",
            gap: "5px",
          }}
        >
          <button
            onClick={() => handlePageChange(1)}
            disabled={pagination.page === 1}
            style={{
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: pagination.page === 1 ? "not-allowed" : "pointer",
            }}
          >
            처음
          </button>
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            style={{
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: pagination.page === 1 ? "not-allowed" : "pointer",
            }}
          >
            이전
          </button>
          <span style={{ padding: "8px 12px" }}>
            {pagination.page} / {pagination.totalPages}
          </span>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
            style={{
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor:
                pagination.page === pagination.totalPages
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            다음
          </button>
          <button
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={pagination.page === pagination.totalPages}
            style={{
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor:
                pagination.page === pagination.totalPages
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            마지막
          </button>
        </div>
      )}

      {/* 등록/수정 모달 */}
      <Modal
        title={staffData.isEdit ? "의료진 수정" : "의료진 등록"}
        className="terminal-info mt-2"
        visible={showModal}
        modalClassName="modal-lg"
        confirmLabel={staffData.isEdit ? "수정" : "등록"}
        onConfirm={handleSubmit}
        onClose={handleCloseModal}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
          }}
        >
          {/* 연결할 사용자 - 전체 너비 */}
          {!staffData.isEdit && (
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="form-label fw-bold">연결할 사용자 *</label>
              <div className="d-flex gap-2">
                <input
                  type="text"
                  value={
                    staffData.userName ||
                    (staffData.userIdx ? `사용자 ID: ${staffData.userIdx}` : "")
                  }
                  readOnly
                  placeholder="사용자를 선택해주세요"
                  className="form-control"
                  style={{ background: "#f8f9fa" }}
                />
                <button
                  className="btn btn-primary"
                  onClick={() => setShowUserSearchModal(true)}
                >
                  검색
                </button>
              </div>
            </div>
          )}

          {/* 의료진 ID */}
          {!staffData.isEdit && (
            <div>
              <label className="form-label fw-bold">의료진 ID *</label>
              <input
                type="text"
                value={staffData.staffId}
                onChange={(e) => handleInputChange("staffId", e.target.value)}
                placeholder="예: STAFF001"
                className="form-control"
              />
            </div>
          )}

          {/* 이름 */}
          <div>
            <label className="form-label fw-bold">이름 *</label>
            <input
              type="text"
              value={staffData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              className="form-control"
            />
          </div>

          {/* 전화번호 */}
          <div>
            <label className="form-label fw-bold">
              전화번호{" "}
              {!staffData.isEdit ? (
                "*"
              ) : (
                <span className="fw-normal text-muted">(변경시만)</span>
              )}
            </label>
            <input
              type="tel"
              value={staffData.phoneNumber}
              onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
              placeholder="01012345678"
              className="form-control"
            />
          </div>

          {/* 이메일 */}
          <div>
            <label className="form-label fw-bold">이메일</label>
            <input
              type="email"
              value={staffData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              className="form-control"
            />
          </div>

          {/* 부서 */}
          <div>
            <label className="form-label fw-bold">부서</label>
            <input
              type="text"
              value={staffData.department}
              onChange={(e) => handleInputChange("department", e.target.value)}
              placeholder="예: 응급의학과"
              className="form-control"
            />
          </div>

          {/* 역할 */}
          <div>
            <label className="form-label fw-bold">역할</label>
            <select
              value={staffData.role}
              onChange={(e) => handleInputChange("role", e.target.value)}
              className="form-select"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {/* 우회 레벨 */}
          <div>
            <label className="form-label fw-bold">우회 레벨</label>
            <select
              value={staffData.bypassLevel}
              onChange={(e) =>
                handleInputChange("bypassLevel", parseInt(e.target.value))
              }
              className="form-select"
            >
              {BYPASS_LEVEL_OPTIONS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>

          {/* 일일 우회 한도 */}
          <div>
            <label className="form-label fw-bold">일일 우회 한도</label>
            <input
              type="number"
              value={staffData.maxDailyBypasses}
              onChange={(e) =>
                handleInputChange(
                  "maxDailyBypasses",
                  parseInt(e.target.value) || 5
                )
              }
              min="1"
              max="100"
              className="form-control"
            />
          </div>

          {/* 세션 유지 시간 */}
          <div>
            <label className="form-label fw-bold">세션 유지 시간 (분)</label>
            <input
              type="number"
              value={staffData.sessionDurationMinutes}
              onChange={(e) =>
                handleInputChange(
                  "sessionDurationMinutes",
                  parseInt(e.target.value) || 30
                )
              }
              min="5"
              max="480"
              className="form-control"
            />
          </div>

          {/* 허용 기능 - 전체 너비 */}
          <div style={{ gridColumn: "1 / -1" }}>
            <label className="form-label fw-bold">허용 기능</label>
            <div className="d-flex gap-4 flex-wrap">
              {ALLOWED_FEATURES.map((f) => {
                // 레벨에 따른 비활성화 여부
                const isDisabled =
                  (f.value === "debug_bypass" && staffData.bypassLevel < 2) ||
                  (f.value === "emulator_bypass" && staffData.bypassLevel < 3);

                return (
                  <div
                    key={f.value}
                    className="form-check"
                    style={{ marginRight: "16px" }}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`feature-${f.value}`}
                      checked={staffData.allowedFeatures.includes(f.value)}
                      onChange={() => handleFeatureToggle(f.value)}
                      disabled={isDisabled}
                      style={{ opacity: isDisabled ? 0.5 : 1 }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`feature-${f.value}`}
                      style={{
                        marginLeft: "8px",
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        color: isDisabled ? "#999" : "inherit",
                      }}
                    >
                      {f.label}
                      {isDisabled && (
                        <span
                          className="text-muted"
                          style={{ fontSize: "0.8em" }}
                        >
                          {" "}
                          (레벨 {f.value === "debug_bypass" ? "2" : "3"} 이상)
                        </span>
                      )}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>

      {/* 사용자 검색 모달 */}
      <Modal
        title="사용자 검색"
        className="terminal-info mt-2"
        visible={showUserSearchModal}
        onClose={() => setShowUserSearchModal(false)}
      >
        <div className="mb-3">
          <div className="d-flex gap-2">
            <input
              type="text"
              className="form-control"
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleUserSearch()}
              placeholder="이름 또는 ID로 검색"
            />
            <button
              className="btn btn-primary"
              onClick={handleUserSearch}
              disabled={userSearchLoading}
            >
              {userSearchLoading ? "검색 중..." : "검색"}
            </button>
          </div>
        </div>

        <div
          className="table_area"
          style={{ maxHeight: "300px", overflowY: "auto" }}
        >
          {userSearchResults.length === 0 ? (
            <div className="text-center text-muted py-4">
              검색 결과가 없습니다.
            </div>
          ) : (
            <div className="table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>이름</th>
                    <th>선택</th>
                  </tr>
                </thead>
                <tbody>
                  {userSearchResults.map((user) => (
                    <tr key={user.idx}>
                      <td>{user.id || user.idx}</td>
                      <td>{user.name || "-"}</td>
                      <td>
                        <input
                          type="button"
                          className="btn btn-success btn-sm"
                          onClick={() => handleSelectUser(user)}
                          value="선택"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </SectionContainer>
  );
};

export default MedicalStaffPage;
